import { CSRF_HEADER, type WikiNotification, type WikiNotificationList } from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineError } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { api } from "../api.ts";
import { ok } from "../api-errors.ts";
import { formatDateTime } from "../i18n/format.ts";
import {
  changeSetPath,
  MY_FEEDBACK_PATH,
  navigate,
  projectTaskPath,
  wikiPath,
} from "../router.tsx";
import { useSession } from "../session.tsx";
import { calendarDate } from "../tasks/parts.tsx";

// The notification bell (wiki-collaboration D18, spec `web-shell` "Notification bell in the
// shell"): the viewer's unread count and their last 50 wiki notifications, rendered for them by
// the server. Refreshed when the window regains focus and on load, like the review count, and
// every minute while the page is visible (user decision 2026-10-30, open to change).

/** How often the count refreshes while the page is visible. */
export const NOTIFICATION_POLL_MS = 60_000;

export const notificationKeys = { list: ["me", "wiki-notifications"] as const };
const NOTIFICATION_QUERY = { include: "tasks", feedback: "1", agent: "1" } as const;

export function useNotifications() {
  return useQuery({
    queryKey: notificationKeys.list,
    retry: false,
    refetchInterval: NOTIFICATION_POLL_MS,
    refetchIntervalInBackground: false,
    // `include=tasks` (tasks-notifications), `feedback=1` (feedback-reporter-status) and
    // `agent=1` (wiki-resolve-with-agent D11): an older API ignores them and lists wiki entries.
    queryFn: async (): Promise<WikiNotificationList> =>
      ok(await api.api.me["wiki-notifications"].$get({ query: NOTIFICATION_QUERY })),
  });
}

function useMarkRead() {
  const queryClient = useQueryClient();
  const { csrfToken } = useSession();
  return useMutation({
    mutationFn: async (ids: string[] | null): Promise<WikiNotificationList> =>
      ok(
        await api.api.me["wiki-notifications"].read.$post(
          { query: NOTIFICATION_QUERY, json: ids ? { ids } : {} },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: (list) => queryClient.setQueryData(notificationKeys.list, list),
  });
}

/** `path` with `?thread=` (or `&thread=`) when the entry is about a thread. */
const withThread = (path: string, threadId: string | null) =>
  threadId
    ? `${path}${path.includes("?") ? "&" : "?"}thread=${encodeURIComponent(threadId)}`
    : path;

/**
 * Where an entry leads: its task, its change set's review, else its page, at its thread's
 * passage when it has one (web-shell "Thread deep links"); nowhere once access is lost. The
 * review or the page opens the thread only when the viewer may see it.
 */
export function targetOf(n: WikiNotification): string | null {
  if (n.subject === "feedback") {
    return MY_FEEDBACK_PATH;
  }
  // Every other entry has a project; one without (an unknown subject) opens nothing.
  if (n.projectId === null) {
    return null;
  }
  if (n.subject === "task") {
    return n.taskId ? projectTaskPath(n.projectId, n.taskId) : null;
  }
  if (n.changeSetId) {
    return withThread(changeSetPath(n.projectId, n.changeSetId), n.threadId);
  }
  return n.pageId ? withThread(wikiPath(n.projectId, n.pageId), n.threadId) : null;
}

/** Today or tomorrow by the UTC calendar day, as task due dates are read everywhere. */
export function dueIn(day: string, now = new Date()): "today" | "tomorrow" | "past" | null {
  const today = now.toISOString().slice(0, 10);
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  return day === today ? "today" : day === tomorrow ? "tomorrow" : day < today ? "past" : null;
}

/** The bell and its sheet. `className` places the button (sidebar header or phone bar). */
export function NotificationBell({ className }: { className?: string }) {
  const { t, i18n } = useTranslation();
  const list = useNotifications();
  const mark = useMarkRead();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const leaving = useRef(false);
  // An N-1 API has no notifications route: no bell rather than a broken one.
  if (list.isError) {
    return null;
  }
  const unread = list.data?.unread ?? 0;
  const items = list.data?.items ?? [];
  const label = unread ? t("notifications.bellUnread", { count: unread }) : t("notifications.bell");
  const sentence = (n: WikiNotification) => {
    if (n.subject === "feedback") {
      return feedbackSentence(n);
    }
    if (n.subject === "task") {
      return taskSentence(n);
    }
    const subject = n.title
      ? t("notifications.quoted", { title: n.title })
      : t(n.subject === "page" ? "notifications.page" : "notifications.changeSet");
    if (n.kind === "agent_replies_sent" && n.agentReplies) {
      // "The agent answered 3 of your comments on 'Pump refresh' · 1 question for you" (D11).
      const { replies, questions } = n.agentReplies;
      return [
        t("notifications.agentReplies", { count: replies, subject }),
        ...(questions ? [t("notifications.agentQuestions", { count: questions })] : []),
      ].join(" · ");
    }
    return t(`notifications.kind.${n.kind}`, {
      actor: n.actorName ?? t("notifications.someone"),
      subject,
    });
  };
  // The caller's own report (feedback-reporter-status): its type and the status it reached.
  const feedbackSentence = (n: WikiNotification) => {
    const f = n.feedback;
    if (!f) {
      return t("notifications.kind.feedback_status");
    }
    return f.status === "fixed" && f.fixedIn
      ? t(`notifications.feedback.${f.type}.fixedIn`, { version: f.fixedIn })
      : t(`notifications.feedback.${f.type}.${f.status}`);
  };
  // Task entries (tasks-notifications): the title and date only as the server returns them.
  const taskSentence = (n: WikiNotification) => {
    const quoted = n.title ? t("notifications.quoted", { title: n.title }) : null;
    if (n.kind === "task_assigned") {
      return t("notifications.kind.task_assigned", {
        actor: n.actorName ?? t("notifications.someone"),
        subject: quoted ?? t("notifications.task"),
      });
    }
    const subject = quoted ?? t("notifications.taskStart");
    const when = n.dueDate ? dueIn(n.dueDate) : null;
    if (when === "today" || when === "tomorrow") {
      return t(`notifications.kind.task_due_${when}`, { subject });
    }
    // A reminder kept after its date reads in the past, never "is due" a passed day.
    return n.dueDate
      ? t(when === "past" ? "notifications.kind.task_was_due" : "notifications.kind.task_due_on", {
          subject,
          date: calendarDate(n.dueDate, i18n.language),
        })
      : t("notifications.kind.task_due_soon", { subject });
  };
  return (
    <>
      <Button
        ref={trigger}
        variant="ghost"
        size="icon"
        aria-label={label}
        aria-haspopup="dialog"
        className={cn("relative size-7 text-fg-muted hover:text-fg", className)}
        onClick={() => setOpen(true)}
      >
        <Bell aria-hidden="true" />
        {unread ? (
          <span
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-fg px-1 text-[10px] leading-none font-medium text-fg-inverse tabular-nums"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        ) : null}
      </Button>
      {/* The count, announced when it changes (the button's name says it on focus). */}
      <span className="sr-only" aria-live="polite">
        {unread ? t("notifications.bellUnread", { count: unread }) : ""}
      </span>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-[min(24rem,100vw)] gap-0 p-0"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            // Leaving for an entry's page: focus belongs to that page, not the bell.
            if (!leaving.current) {
              trigger.current?.focus();
            }
            leaving.current = false;
          }}
        >
          <SheetHeader className="border-b border-line">
            <SheetTitle>{t("notifications.title")}</SheetTitle>
            <SheetDescription>{t("notifications.description")}</SheetDescription>
          </SheetHeader>
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            {items.length ? (
              // Page titles, people and project names. The sheet is a portal outside the capture;
              // marked anyway, so it stays masked wherever it is rendered.
              <ul data-esi-content="wiki" className="divide-y divide-line">
                {items.map((n) => {
                  const href = targetOf(n);
                  const body = (
                    <>
                      <span
                        aria-hidden="true"
                        className={cn(
                          "mt-1.5 size-2 shrink-0 rounded-full",
                          n.read ? "bg-transparent" : "bg-fg",
                        )}
                      />
                      <span className="flex min-w-0 flex-col gap-0.5">
                        <span
                          className={cn(
                            "text-sm break-words",
                            n.read ? "text-fg-secondary" : "font-medium text-fg",
                          )}
                        >
                          {n.read ? null : (
                            <span className="sr-only">{t("notifications.unread")}: </span>
                          )}
                          {sentence(n)}
                        </span>
                        {n.feedback?.reason ? (
                          // The operator's reason, as plain text (feedback-reporter-status D4).
                          <span className="text-sm break-words text-fg-secondary">
                            {t("notifications.quoted", { title: n.feedback.reason })}
                          </span>
                        ) : null}
                        <span className="text-xs text-fg-muted">
                          {n.subject === "feedback"
                            ? `${t("notifications.feedbackFrom")} · `
                            : n.projectName
                              ? `${n.projectName} · `
                              : ""}
                          <time dateTime={n.createdAt}>{formatDateTime(n.createdAt)}</time>
                        </span>
                      </span>
                    </>
                  );
                  return (
                    <li key={n.id}>
                      {href ? (
                        <a
                          href={href}
                          className="flex gap-3 px-4 py-3 text-left hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none"
                          onClick={(e) => {
                            if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) {
                              return;
                            }
                            e.preventDefault();
                            if (!n.read) {
                              mark.mutate([n.id]);
                            }
                            leaving.current = true;
                            setOpen(false);
                            navigate(href);
                          }}
                        >
                          {body}
                        </a>
                      ) : (
                        <button
                          type="button"
                          className="flex w-full gap-3 px-4 py-3 text-left hover:bg-surface-hover"
                          onClick={() => (n.read ? undefined : mark.mutate([n.id]))}
                        >
                          {body}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="px-4 py-8 text-center text-sm text-fg-secondary">
                {t("notifications.empty")}
              </p>
            )}
          </div>
          {unread ? (
            <div className="flex flex-col gap-2 border-t border-line px-4 py-3">
              <Button
                variant="outline"
                size="sm"
                disabled={mark.isPending}
                onClick={() => mark.mutate(null)}
              >
                {t("notifications.markAll")}
              </Button>
              {mark.isError ? <InlineError>{mark.error.message}</InlineError> : null}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}
